import axios, { AxiosError, AxiosRequestConfig, AxiosRequestHeaders } from 'axios';
import { API_BASE, AUTH_BASE } from './constants';

export type ApiClientRequestConfig = AxiosRequestConfig & {
  _retry?: boolean;
  _skipAuthRefresh?: boolean;
};

let refreshPromise: Promise<string> | null = null;

const getStoredToken = () => localStorage.getItem('adminAccessToken');
const setStoredToken = (token: string) => localStorage.setItem('adminAccessToken', token);
const clearStoredToken = () => localStorage.removeItem('adminAccessToken');
const markSessionExpired = () => localStorage.setItem('sessionExpired', '1');

const handleSessionExpired = () => {
  markSessionExpired();
  clearStoredToken();
  window.location.assign('/');
};

const refreshToken = async () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const token = getStoredToken();
    if (!token) {
      throw new Error('Missing token');
    }

    const response = await axios.post(
      `${AUTH_BASE}/refresh`,
      null,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const accessToken = response.data?.accessToken;
    if (!accessToken) {
      throw new Error('Missing access token');
    }

    setStoredToken(accessToken);
    return accessToken as string;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
};

export const apiClient = axios.create({
  baseURL: API_BASE,
});

apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = {
      ...(config.headers || {}),
      Authorization: `Bearer ${token}`,
    } as AxiosRequestHeaders;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const response = error.response;
    const config = error.config as ApiClientRequestConfig | undefined;

    if (!response || !config) {
      return Promise.reject(error);
    }

    if (config._skipAuthRefresh) {
      return Promise.reject(error);
    }

    const payload = response.data as { error?: string; code?: string } | undefined;
    const errorCode = payload?.error || payload?.code;

    if (response.status !== 401 || errorCode !== 'INVALID_TOKEN') {
      return Promise.reject(error);
    }

    if (config._retry) {
      handleSessionExpired();
      return Promise.reject(error);
    }

    config._retry = true;

    try {
      const newToken = await refreshToken();
      config.headers = { ...config.headers, Authorization: `Bearer ${newToken}` };
      return await apiClient(config);
    } catch (refreshError) {
      handleSessionExpired();
      return Promise.reject(error);
    }
  }
);
